import { createApi } from '@reduxjs/toolkit/query/react';
import { request, gql, ClientError } from 'graphql-request';
import firebase from 'firebase/app';
import { createClient } from 'graphql-ws';
import stringifyObject from 'stringify-object';
import { upperFirst } from 'lodash';

import { graphqlConfig } from '../../config';

import { consultancySettingsType } from '../graphqlTypes';

// we must send getToken from inside the createApi, otherwise firebase.auth() doesn't exist
// we use a function so when it disconnect because of old token it will get a new one
export const ws = (() => {
  let client = false;
  return {
    getClient: async (currentUser) => {
      if (!client) {
        client = createClient({
          url: graphqlConfig.wss,
          connectionParams: async () => {
            const token = await currentUser.getIdToken();
            return {
              headers: { authorization: `Bearer ${token}` },
            };
          },
          on: {
            error: (err) => console.log('websocket error', err),
          },
        });
      }
      return client;
    },
  };
})();

export function objectToStr(obj) {
  return stringifyObject(obj, {
    indent: '  ',
    singleQuotes: false,
  });
}

export function splitJsonFields(obj, jsonFields) {
  // split the objects to two objects, one with the fields and the other with the rest
  const result = { jsonFields: {}, regularFields: {} };
  Object.keys(obj).forEach((key) => {
    if (jsonFields.includes(key)) {
      result.jsonFields[key] = obj[key];
    } else {
      result.regularFields[key] = obj[key];
    }
  });
  return result;
}

function getSearchOrFilterString({ name, value, type }) {
  // Split string A into an array of words
  if (value === '') return '';
  const names1 = ['sheet_profiles[0].is_star', 'spec_project_profiles[0].is_star'];
  const names2 = ['is_disabled'];
  const words = name.split('.');

  if (names1.some((_name) => _name === name) && value === false) return '';
  // Create an object to store the nested structure
  let result =
    typeof value === 'boolean'
      ? [...names1].some((_name) => _name === name)
        ? { _eq: true }
        : { _eq: value }
      : { _ilike: `%${value}%` };

  let isArray = false;
  // Iterate over the words array
  // eslint-disable-next-line no-plusplus
  for (let i = words.length - 1; i >= 0; i--) {
    const word = words[i];

    const regex = /(\w+)\[(\d+)\]/;
    const matches = word.match(regex);
    let key = null;
    if (matches) {
      [key] = matches.slice(1);
      isArray = true;
    } else {
      key = word;
    }

    if (type === 'filter' && i === words.length - 1) {
      result = `${key}: ${value}`;
      if (words.length !== 1) result = { [key]: value };
    } else {
      // Create a nested object with the current word as the key
      result = { [key]: result };
    }
  }
  if (value === false && names1.some((_name) => _name === name)) {
    result = { _not: result };
  }
  if (value === false && names2.some((_name) => _name === name)) {
    result = { _or: [{ [name]: { _eq: false } }, { [name]: { _is_null: true } }] };
  }
  /* eslint no-nested-ternary: */
  return type === 'filter'
    ? isArray
      ? ''
      : objectToStr(result).slice(1, -1).replace(/"/g, '')
    : objectToStr(result).slice(1, -1);
}

const graphqlBaseQuery =
  ({ baseUrl }) =>
  async ({ body }) => {
    const token = await firebase.auth().currentUser.getIdToken();
    try {
      const result = await request({
        url: baseUrl,
        document: body,
        requestHeaders: {
          authorization: `Bearer ${token}`,
        },
      });
      return { data: result };
    } catch (error) {
      if (error instanceof ClientError) {
        return { error: { status: error.response.status, data: error } };
      }
      return { error: { status: 500, data: error } };
    }
  };

export function toCacheKey(cacheKey) {
  if (cacheKey) {
    if (typeof cacheKey === 'number') return cacheKey.toString();
    return cacheKey;
  }
  return undefined;
}

function toPascalCase(str) {
  return str.split('_').map(upperFirst).join('');
}

export function escapeBackslashes(input, innerSlash = false) {
  const isStr = typeof input === 'string';

  if (isStr) {
    return input.replace(/\\/g, '\\\\');
  } else {
    const escapeObject = (obj) => {
      if (typeof obj === 'string') {
        if (innerSlash) {
          obj = obj.replace(/\n/g, '\\n').replace(/\t/g, '\\t').replace(/\v/g, '\\v');
        }
        return obj.replace(/\\/g, '\\\\');
      } else if (Array.isArray(obj)) {
        return obj.map(escapeObject);
      } else if (obj && typeof obj === 'object') {
        return Object.fromEntries(
          Object.entries(obj).map(([key, value]) => [key, escapeObject(value)])
        );
      }
      return obj;
    };
    return escapeObject(input);
  }
}

/**
 * Updates the cache before sending data to the server
 * @param {string} endPoint - the cache endpoint name
 * @param {boolean} [isList] - is this an update to an item in an array
 * @returns {Function} - onQueryStarted function
 *
 * in the patch you can provide:
 * @param {string} payload - the actual data to change
 * @param {string} endPointOverride - if you want to update a different endpoint of the cache
 * @param {string} cacheKey - if the endpoint has a specific cache key like an id or a full object of query parameters
 * @param {string} path - to update deep inside a nested  object
 */
export function beforeUpdate(endPoint, isList) {
  return async (patch, { dispatch, queryFulfilled }) => {
    const id = isList ? patch.cacheKey : patch.id;
    const patchResult = dispatch(
      baseApi.util.updateQueryData(patch.endPointOverride || endPoint, toCacheKey(id), (draft) => {
        if (patch.path) {
          const refToNested = patch.path.reduce((o, i) => o[i], draft);
          const i = refToNested.findIndex((x) => x.id === patch.id);
          Object.assign(refToNested[i], patch.payload);
          return draft;
        }
        if (isList) {
          const i = draft.findIndex((x) => x.id === patch.id);
          Object.assign(draft[i], patch.payload);
        } else {
          Object.assign(draft, patch.payload);
        }
      })
    );
    try {
      await queryFulfilled;
    } catch (error) {
      patchResult.undo();
      console.log(error);
    }
  };
}

export function beforeUpdateSheetRow(endPoint) {
  return async (patch, { dispatch, queryFulfilled }) => {
    const patchResult = dispatch(
      baseApi.util.updateQueryData(
        patch.endPointOverride || endPoint,
        toCacheKey(patch.cacheKey),
        (draft) => {
          const i = draft.findIndex((x) => x.id === patch.id);
          Object.assign(draft[i], {
            ...patch.payload,
            ...(patch.custom || {}),
          });
        }
      )
    );
    try {
      await queryFulfilled;
    } catch (error) {
      patchResult.undo();
      console.log(error);
    }
  };
}

export function afterMultipleUpdate(endPoint) {
  return async ({ cacheKey }, { dispatch, queryFulfilled }) => {
    try {
      const { data } = await queryFulfilled;
      dispatch(
        baseApi.util.updateQueryData(endPoint, toCacheKey(cacheKey), (draft) => {
          data.forEach((row) => {
            let rowId = row?.returning?.[0].id ?? row.id;
            let update = row?.returning?.[0] ?? row;
            const i = draft.findIndex((x) => x.id === rowId);
            Object.assign(draft[i], update);
          });
        })
      );
    } catch (error) {
      console.log(error);
    }
  };
}

export function afterUpdate(endPoint) {
  return async ({ cacheKey }, { dispatch, queryFulfilled }) => {
    try {
      const { data } = await queryFulfilled;
      // const queryState = baseApi.endpoints;

      dispatch(
        baseApi.util.updateQueryData(endPoint, toCacheKey(cacheKey), (draft) => {
          const i = draft.findIndex((x) => x.id === data.id);
          Object.assign(draft[i], data);
        })
      );
    } catch (error) {
      console.log(error);
    }
  };
}

export function afterInsert(endPoint) {
  return async ({ endPointOverride, cacheKey, path, addToTop }, { dispatch, queryFulfilled }) => {
    try {
      const { data } = await queryFulfilled;
      dispatch(
        baseApi.util.updateQueryData(
          endPointOverride ?? endPoint,
          toCacheKey(cacheKey),
          (draft) => {
            const arr = Array.isArray(data) ? data : [data];
            if (path) {
              const refToNested = path.reduce((o, i) => o[i], draft);
              Object.assign(refToNested, refToNested.concat(arr));
              return draft;
            }
            if (addToTop) {
              return arr.concat(draft);
            }
            return draft.concat(arr);
          }
        )
      );
    } catch (error) {
      console.log(error);
    }
  };
}

export function afterRemove(endPoint) {
  return async ({ endPointOverride, cacheKey, path }, { dispatch, queryFulfilled }) => {
    try {
      const { data } = await queryFulfilled;
      dispatch(
        baseApi.util.updateQueryData(
          endPointOverride || endPoint,
          toCacheKey(cacheKey),
          (draft) => {
            if (path) {
              const refToNested = path.reduce((o, i) => o[i], draft);
              const i = refToNested.findIndex((x) => x.id === data.id);
              refToNested.splice(i, 1);
              return draft;
            }
            const i = draft.findIndex((x) => x.id === data.id);
            draft.splice(i, 1);
          }
        )
      );
    } catch (error) {
      console.log(error);
    }
  };
}

export function afterInsertSheetRow() {
  return async (patch, { dispatch, queryFulfilled }) => {
    try {
      const { data } = await queryFulfilled;
      dispatch(
        baseApi.util.updateQueryData('getSheetRows', data.sheet_id.toString(), (draft) => {
          draft.push(data);
        })
      );
      dispatch(
        baseApi.util.updateQueryData('getSheet', data.sheet_id.toString(), (draft) => {
          draft.seq.splice(patch.payload.below_index + 1, 0, data.id);
        })
      );
    } catch (error) {
      console.log(error);
    }
  };
}

export function afterInsertSpecSection(
  updateQuery = 'getSpecSections',
  getId = (data) => (data?.returning?.length ? data.returning[0].spec_id : data?.spec_id)
) {
  return async (patch, { dispatch, queryFulfilled }) => {
    try {
      const { data } = await queryFulfilled;
      const id = getId(data);
      if (id) {
        dispatch(
          baseApi.util.updateQueryData(updateQuery, id?.toString(), (draft) => {
            draft.push(data);
          })
        );
      }
    } catch (error) {
      console.log(error);
    }
  };
}

export function afterInsertSpecRevisionSection() {
  return async (patch, { dispatch, queryFulfilled }) => {
    try {
      const specId = patch.specId;
      const { data } = await queryFulfilled;
      if (data?.returning?.length) {
        let revId = data.returning[0].spec_revision_id;
        dispatch(
          baseApi.util.updateQueryData('getSpecRevisions', { specId }, (draft) => {
            Object.assign(
              draft,
              draft.map((d) => {
                if (d.id === revId) {
                  return {
                    ...d,
                    spec_revision_sections: data.returning,
                  };
                }

                return d;
              })
            );
          })
        );
      }
    } catch (error) {
      console.log(error);
    }
  };
}

export function buildPaginationClauses({
  params,
  hasSearch,
  searchDefaults = { limit: 100, offset: 0 },
  listDefaults = { limit: 100, offset: 0 },
}) {
  const rawLimit = Number.isFinite(params?.limit) ? params.limit : undefined;
  const rawOffset = Number.isFinite(params?.offset) ? params.offset : undefined;

  const limitVal =
    typeof rawLimit === 'number' ? rawLimit : hasSearch ? searchDefaults.limit : listDefaults.limit;
  const offsetVal =
    typeof rawOffset === 'number'
      ? rawOffset
      : hasSearch
      ? searchDefaults.offset
      : listDefaults.offset;

  return [`limit: ${limitVal}`, `offset: ${offsetVal}`];
}

export function afterSeqUpdate(endPoint) {
  return async ({ payload }, { dispatch, queryFulfilled }) => {
    try {
      const { data } = await queryFulfilled;
      dispatch(
        baseApi.util.updateQueryData(endPoint, toCacheKey(payload.sheet_id), (draft) => {
          Object.assign(draft, data[0]);
        })
      );
    } catch (error) {
      console.log(error);
    }
  };
}

export function insert(
  builder,
  table,
  modelType,
  onQueryStarted,
  invalidatesTags,
  bulk,
  escapeInnerSlash = false
) {
  return builder.mutation({
    query: (params) => {
      params = escapeBackslashes(params, escapeInnerSlash);
      return {
        body: gql`
        mutation {
          insert_${table}${bulk ? '' : '_one'}(object${bulk ? 's' : ''}: ${objectToStr(
          params.payload
        )})  ${modelType}
        }
        `,
      };
    },
    transformResponse: (response) => response[`insert_${table}${bulk ? '' : '_one'}`],
    onQueryStarted,
    invalidatesTags,
  });
}

export function remove(builder, table, modelType, onQueryStarted, invalidatesTags) {
  return builder.mutation({
    query: (params) => {
      const id = typeof params.id === 'number' ? params.id : `"${params.id}"`;
      return {
        body: gql`
      mutation {
        delete_${table}_by_pk(id: ${id})  ${modelType}
      }
      `,
      };
    },
    transformResponse: (response) => response[`delete_${table}_by_pk`],
    onQueryStarted,
    invalidatesTags,
  });
}

export function update(
  builder,
  table,
  modelType,
  onQueryStarted,
  invalidatesTags,
  escapeInnerSlash = false
) {
  return builder.mutation({
    query: (params) => {
      params = escapeBackslashes(params, escapeInnerSlash);
      const id = typeof params.id === 'number' ? params.id : `"${params.id}"`;
      return {
        body: gql`
      mutation {
        update_${table}_by_pk(pk_columns: {id: ${id}}, _set: 
          ${objectToStr(params.payload)}
        )  ${modelType}
      }
      `,
      };
    },
    transformResponse: (response) => response[`update_${table}_by_pk`],
    onQueryStarted,
    invalidatesTags,
  });
}

/**
 * if the paramToDbColumn is a function, it will just override anything and use it as the where clause
 * if not it will look at the filter object and map the keys to the db columns
 * so when sending params we should also map them in the filter to the right db column
 */
function createFilter(paramToDbColumn, params) {
  if (typeof paramToDbColumn === 'function') return `where: ${paramToDbColumn(params)}`;

  const where = Object.keys(params)
    .map((k) => {
      const value = `${typeof params[k] === 'string' ? `"${params[k]}"` : params[k]}`;

      if (Array.isArray(params[k])) {
        const [op, value] = params[k];

        if (op === '_ilike') {
          return `${k}: { ${op}: "%${value}%" }`;
        }

        return `${k}: { ${op}: ${value} }`;
      }

      if (params[k] === null) {
        return `${paramToDbColumn[k]}: { _is_null: true }`;
      }

      return `${paramToDbColumn[k]}: { _eq: ${value} }`;
    })
    .join(',');
  return where.length > 0 ? `where: {${where}}` : '';
}

/**
 * @typedef {Object} SelectOptions
 * @property {Object} [query] - a full endpoint query, will run it as is
 * @property {string} [where] - static where clause
 * @property {function(Object):void} [filter] - a function which accept params object and generate the where clause
 * @property {string} [order] - the order by clause
 * @property {string} [limit] - the limit clause
 * @property {string} [response] - the response function
 * @property {Array|string} [tags] -  custom tags
 * @property {Object|string} [cacheKey] -  custom cachekey, use for when you want all queries to be on the same cache even if the where parameters are different
 */

/**
 *
 * @param {function} builder - just send the api builder
 * @param {string} table - name of the table in the database
 * @param {string} modelType - the graphQL model to return
 * @param {boolean} [isById=flase] - if true it runs a select by id and return only one object
 * @param {SelectOptions} [options] - both query and endpoint options
 * @returns {Object} - endpoint
 */

export function select(builder, table, modelType, isById = false, options) {
  let _table = table;
  let _modelType = modelType;

  if (isById && !options?.query) {
    return builder.query({
      query: (id) => ({
        body: gql`
        query {
          ${table}_by_pk(id: ${id}) ${modelType}
        }
        `,
      }),
      transformResponse: options?.response
        ? options.response
        : (response) => response[`${table}_by_pk`],
      providesTags: (result, error, id) => options?.tags || [{ type: toPascalCase(table), id }],
    });
  }

  let where = '';
  if (options?.where) where = `where: ${options.where}`;

  let order = '';
  if (typeof options?.order === 'string') order = `order_by: ${options.order}`;

  let limit = '';
  if (options?.limit) limit = `limit: ${options.limit.toString()}`;

  return builder.query({
    query: options?.query
      ? options.query
      : (params) => {
          const allClauses = [];
          const whereClause =
            params && options?.filter ? createFilter(options.filter, params) : where;
          if (whereClause.length > 0) allClauses.push(whereClause);
          if (typeof options?.order === 'function') order = `order_by: ${options.order(params)}`;
          if (order.length > 0) allClauses.push(order);
          if (options?.limit) allClauses.push(limit);

          _table = params?.table || table;
          _modelType = params?.modelType || modelType;

          return {
            body: gql`
            query {
              ${_table} ${allClauses.length > 0 ? `(${allClauses.join(', ')})` : ''} ${_modelType}
            }
          `,
          };
        },
    transformResponse: options?.response
      ? options.response
      : (response, meta, arg) => response[arg?.table || _table],
    // eslint-disable-next-line arrow-body-style
    providesTags: () => {
      return options?.tags || [toPascalCase(_table)];
    },
    ...(options?.cacheKey && {
      forceRefetch: ({ currentArg, previousArg }) => currentArg !== previousArg,
      serializeQueryArgs: (params) =>
        typeof options.cacheKey === 'function' ? options.cacheKey(params) : options.cacheKey,
    }),
  });
}

export const baseApi = createApi({
  reducerPath: 'api',
  baseQuery: graphqlBaseQuery({ baseUrl: graphqlConfig.https }),
  tagTypes: [
    'Profile',
    'Product',
    'SpecProject',
    'Sheet',
    'Spec',
    'SpecScope',
    'SpecShowScope',
    'SpecSection',
    'SpecSectionDiscussion',
    'SpecSectionComment',
    'SpecRevision',
    'SpecRevisionSection',
    'SheetProfile',
    'SpecProjectProfile',
    'SheetRow',
    'Company',
    'ConstructionProject',
    'SpecProjectParam',
    'SheetRowComment',
    'SheetComment',
    'ProjectUpdate',
    'Revision',
    'NotificationProfile',
    'ProjectProfile',
    'SpecProjectCalendar',
    'Task',
    'Invitation',
    'ConsultancySettings',
    'ProductComment',
    'PublicSheet',
    'Template',
    'SheetRowAudit',
    'Uniclass',
    'Standard',
    'AnalyticsNominationsTotal',
    'AnalyticsNominationsDetail',
    'AnalyticsProjectsValue',
    'ProfileGroup',
    'ProductCompany',
    'CompanyContact',
  ],
  endpoints: (builder) => ({
    getAutoCompleteFilter: builder.query({
      query: (params) => ({
        body: gql`
        query {
          ${params.table}(
            where: ${params.filter}
            order_by: { ${params.sortBy.column}: ${params.sortBy.order} }
            limit: 100
          ) {${params.columns.map((c) => c).join('\n')}}
        }
        `,
      }),
      transformResponse: (response, meta, arg) => response[arg.table],
      forceRefetch: ({ currentArg, previousArg }) => currentArg !== previousArg,
      serializeQueryArgs: ({ queryArgs }) => queryArgs.cacheKey || { table: queryArgs.table },
    }),
    getConsultancySettings: select(
      builder,
      'consultancy_settings',
      consultancySettingsType,
      false,
      {
        response: (response) =>
          response.consultancy_settings.reduce((acc, item) => {
            acc[item.key] = item.value;
            return acc;
          }, {}),
      }
    ),
    searchTableTemplate: select(builder, '', '', false, {
      cacheKey: (params) => `searchTableTemplate(${params?.queryArgs?.table})`,
      filter: (params) => {
        let str = '';
        if (params.searchParams.length) {
          // eslint-disable-next-line array-callback-return
          params.searchParams.map((search) => {
            str = str.concat(getSearchOrFilterString(search));
          });
        }

        if (params.where) {
          const where = objectToStr(params.where);
          str = `${str}${where.substring(1, where.length - 2)}`;
        }

        let pageIndex = 0;
        if (params?.pageNation?.pageIndex)
          pageIndex = `offset: ${params.pageNation.pageIndex * params.pageNation.perPage}`;
        else pageIndex = `offset: 0`;

        const limit = params?.pageNation?.perPage ? `limit: ${params.pageNation.perPage}` : '';
        return `{ ${str} }, ${pageIndex}, ${limit}`;
      },
      order: (params) => {
        const orderQuery = params.orderParams?.orderBy
          ? getSearchOrFilterString({
              name: params.orderParams?.orderBy,
              value: params.orderParams?.order,
              type: 'filter',
            })
          : '';
        return `{${orderQuery}}`;
      },
    }),
  }),
});

export const {
  useGetAutoCompleteFilterQuery,
  useGetConsultancySettingsQuery,
  useSearchTableTemplateQuery,
} = baseApi;
